import * as amplitude from "@amplitude/analytics-browser";
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Radio,
  Spacer,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { fromZonedTime } from "date-fns-tz";
import { Form, FormikProps, getIn } from "formik";
import {
  InputControl,
  RadioGroupControl,
  SelectControl,
  TextareaControl,
} from "formik-chakra-ui";
import { useMemo } from "react";
import { Loading } from "src/components/Feedback/Loading";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { InputControlWithParsing } from "src/components/Inputs/InputControlWithParsing";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { LearnMore } from "src/components/Links/LearnMore";
import { CopyToClipboard } from "src/components/Text/CopyToClipboard";
import { useTimezoneName } from "src/hooks/useOrganization";
import useUser from "src/hooks/useUser";
import { useEnv } from "src/services/env";
import {
  extractFolderIdFromUrl,
  extractSpreadsheetIdFromUrl,
} from "src/services/googleDrive";
import { Status } from "src/types/authData";
import {
  GoogleDriveConfigType,
  SFTPAuthMethod,
} from "../../dataServices/exports/types";
import { DateTimePicker } from "./DateTimePicker";
import {
  getPresetTimeOptions,
  PresetTimeOption,
  REPEAT_MODE_OPTIONS,
  repeatModes,
} from "./dateUtils";
import {
  BulkExportFormType,
  ExportFormPage,
  ExportTimeMode,
  getSelectedTimeInTimezone,
} from "./types";

interface ConfigureExportPageProps {
  setCurrentPage: React.Dispatch<React.SetStateAction<ExportFormPage>>;
  isEditingScheduledExport: boolean;
  disableEditSFTP: boolean;
  setDisableEditSFTP: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ConfigureExportPage = (
  props: FormikProps<BulkExportFormType> & ConfigureExportPageProps
) => {
  const {
    isEditingScheduledExport,
    disableEditSFTP,
    setDisableEditSFTP,
    dirty,
    errors,
    touched,
    submitCount,
  } = props;

  const showFieldError = (path: string): boolean =>
    Boolean(getIn(errors, path) && (getIn(touched, path) || submitCount > 0));

  const disabledFromNoChanges = isEditingScheduledExport && !dirty;
  const submitDisabled = disabledFromNoChanges || props.isSubmitting;

  const user = useUser();

  const timezoneName = useTimezoneName();
  const presetTimeOptions: PresetTimeOption[] = useMemo(() => {
    const currentTime = new Date();
    return getPresetTimeOptions(currentTime, timezoneName);
  }, [timezoneName]);

  const googleServiceAccount = useEnv().VITE_GOOGLE_SERVICE_ACCOUNT;

  return (
    <Flex as={Form} direction="column" flexGrow="1" gap={3}>
      {user.status === Status.LOADING && <Loading />}
      {user.status === Status.OK && (
        <>
          <>
            <Heading size="lg">Set a name</Heading>
            <FormControl>
              <FormLabel>Export name </FormLabel>
              <InputControl name="exportName">
                <Input placeholder="Submissions_May" />
              </InputControl>
              <FormHelperText>
                Will result in a file called '
                {props.values.exportName.length > 0
                  ? `${props.values.exportName}_<timestamp>.csv'`
                  : "form_<timestamp>.csv' (default)"}
              </FormHelperText>
            </FormControl>
          </>

          <Heading size="lg" mt={4}>
            Set delivery
          </Heading>
          <Text>
            Once processed, the file will download automatically. How would you
            like to receive the export?
          </Text>
          <Flex direction="column" gap={0}>
            <FormikSwitchInput<boolean>
              label="Email"
              name="emailEnabled"
              formLabelProps={{
                flexDirection: "row-reverse",
                fontSize: "md",
              }}
              checked={[true, ""]}
              isDisabled
            />
            <Text ml={12} fontSize="sm">
              You’ll receive an email with the export on the email address from
              your account.
            </Text>
          </Flex>
          <Flex direction="column" gap={0}>
            <FormikSwitchInput<boolean>
              label="SFTP upload"
              name="sftp.enabled"
              formLabelProps={{
                flexDirection: "row-reverse",
                fontSize: "md",
              }}
              checked={[true, ""]}
              onChange={(event) => {
                amplitude.track("Toggled SFTP upload", {
                  sftp_upload: event.target.checked
                    ? "is_active"
                    : "is_inactive",
                });
              }}
            />
            <Text ml={12} fontSize="sm">
              We will upload the export to an external server through a secure
              connection. The receiving server or endpoint will need to have
              authorized Avela.
            </Text>
          </Flex>
          {props.values.sftp.enabled && (
            <Flex ml={12} direction="column" gap={4}>
              <Flex gap={4}>
                <InputControl
                  name="sftp.config.host"
                  label="Host"
                  flex="3 1 0"
                  inputProps={{
                    placeholder: "E.g.: server.example.com or 192.168.1.1",
                  }}
                  isDisabled={disableEditSFTP}
                />
                <InputControl
                  name="sftp.config.port"
                  label="Port"
                  flex="1 1 0"
                  inputProps={{
                    placeholder: "E.g.: 22",
                    type: "number",
                  }}
                  isDisabled={disableEditSFTP}
                />
                <InputControl
                  name="sftp.config.path"
                  label="Path"
                  flex="4 1 0"
                  inputProps={{
                    placeholder: "E.g.: /users/mysftpuser",
                  }}
                  isDisabled={disableEditSFTP}
                />
              </Flex>
              <SelectControl
                name="sftp.config.authMethod"
                label="Authentication method"
                selectProps={{ placeholder: "Select an option" }}
                w="fit-content"
                isDisabled={disableEditSFTP}
              >
                {[SFTPAuthMethod.Password, SFTPAuthMethod.SSHKey].map(
                  (authMethod) => (
                    <option key={authMethod} value={authMethod}>
                      {authMethod}
                    </option>
                  )
                )}
              </SelectControl>
              {props.values.sftp.config?.authMethod && (
                <Flex gap={4}>
                  <InputControl
                    name="sftp.config.username"
                    label="Username"
                    inputProps={{
                      placeholder: "Enter username",
                    }}
                    isDisabled={disableEditSFTP}
                  />
                  {props.values.sftp.config.authMethod ===
                    SFTPAuthMethod.Password && (
                    <InputControl
                      name="sftp.config.secret"
                      label="Password"
                      inputProps={{
                        placeholder: "Enter password",
                      }}
                      isDisabled={disableEditSFTP}
                    />
                  )}
                  {props.values.sftp.config.authMethod ===
                    SFTPAuthMethod.SSHKey && (
                    <TextareaControl
                      name="sftp.config.secret"
                      label="Private key"
                      textareaProps={{
                        rows: 10,
                        size: "sm",
                        placeholder:
                          "-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----",
                      }}
                      isDisabled={disableEditSFTP}
                    />
                  )}
                </Flex>
              )}
              {disableEditSFTP && (
                <Flex alignItems="center">
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => setDisableEditSFTP(false)}
                  >
                    Edit SFTP configuration
                  </Button>
                  <Text ml={4} fontSize="sm">
                    {props.values.sftp.config?.authMethod ===
                    SFTPAuthMethod.Password
                      ? "Password"
                      : "Private key"}{" "}
                    is hidden for security reasons. To update your SFTP
                    configuration, you will need to re-enter it.
                  </Text>
                </Flex>
              )}
            </Flex>
          )}
          <Flex direction="column" gap={0}>
            <FormikSwitchInput<boolean>
              label="Google Drive"
              name="googleDrive.enabled"
              formLabelProps={{
                flexDirection: "row-reverse",
                fontSize: "md",
              }}
              checked={[true, ""]}
              onChange={(event) => {
                amplitude.track("Toggled Google upload", {
                  google_upload: event.target.checked
                    ? "is_active"
                    : "is_inactive",
                });
              }}
            />
            <Text ml={12} fontSize="sm">
              We will upload the export using a secure service account
              connection. Each file type may have its own requirements for
              sharing.
              {/* TODO: "Learn more" link */}
            </Text>
          </Flex>
          {props.values.googleDrive.enabled && (
            <Flex ml={12} direction="column" gap={4}>
              <FormControl>
                <FormLabel fontSize="md">What file should we use?</FormLabel>
                <RadioGroupControl name="googleDrive.config.type">
                  <Flex gap={4} direction="column" width="100%">
                    <Radio
                      value={GoogleDriveConfigType.UploadCSV}
                      key={GoogleDriveConfigType.UploadCSV}
                      onClick={() => {
                        amplitude.track("Selected Google upload type", {
                          google_upload_type: "google_csv",
                        });
                      }}
                    >
                      <Text>New .CSV</Text>
                      <FormHelperText>
                        Create a .CSV file on a Shared Drive folder. Shared
                        drives are available only to Google Workspace users, and
                        the receiving folder must be shared with our service
                        account <CopyToClipboard text={googleServiceAccount} />{" "}
                        as a Contributor.{" "}
                        <LearnMore article="39078407385741-How-do-I-export-a-new-CSV-to-Google-Drive" />
                      </FormHelperText>
                    </Radio>
                    {props.values.googleDrive.config?.type ===
                      GoogleDriveConfigType.UploadCSV && (
                      <Flex ml={6} alignItems="flex-start" gap={4}>
                        <GoogleDriveFolderIdInput />
                      </Flex>
                    )}
                    <Radio
                      value={GoogleDriveConfigType.CreateSheet}
                      key={GoogleDriveConfigType.CreateSheet}
                      onClick={() => {
                        amplitude.track("Selected Google upload type", {
                          google_upload_type: "google_new_sheet",
                        });
                      }}
                    >
                      <Text>New Google Sheets spreadsheet</Text>
                      <FormHelperText>
                        Create a new spreadsheet on a Shared Drive folder.
                        Spreadsheets have a maximum of 10,000 rows. Shared
                        drives are available only to Google Workspace users, and
                        the receiving folder must be shared with our service
                        account <CopyToClipboard text={googleServiceAccount} />{" "}
                        as a Contributor.{" "}
                        <LearnMore article="39080349763213-How-do-I-export-a-new-Google-Spreadsheet-to-Google-Drive" />
                      </FormHelperText>
                    </Radio>
                    {props.values.googleDrive.config?.type ===
                      GoogleDriveConfigType.CreateSheet && (
                      <Flex ml={6} alignItems="flex-start" gap={4}>
                        <GoogleDriveFolderIdInput />
                        <GoogleSheetsSheetNameInput />
                      </Flex>
                    )}
                    <Radio
                      value={GoogleDriveConfigType.UpdateSheet}
                      key={GoogleDriveConfigType.UpdateSheet}
                      onClick={() => {
                        amplitude.track("Selected Google upload type", {
                          google_upload_type: "google_existing_sheet",
                        });
                      }}
                    >
                      <Text>Existing Google Sheets spreadsheet</Text>
                      <FormHelperText>
                        Update an existing spreadsheet replacing its contents
                        with new data. The receiving file must be shared with
                        our service account{" "}
                        <CopyToClipboard text={googleServiceAccount} /> as an
                        Editor, and has a maximum of 10,000 rows.{" "}
                        <LearnMore article="38840958233485-How-do-I-export-to-an-existing-Google-Spreadsheet" />
                      </FormHelperText>
                    </Radio>
                    {props.values.googleDrive.config?.type ===
                      GoogleDriveConfigType.UpdateSheet && (
                      <Flex ml={6} alignItems="flex-start" gap={4}>
                        <GoogleSheetsSpreadsheetIdInput />
                        <GoogleSheetsSheetNameInput />
                      </Flex>
                    )}
                  </Flex>
                </RadioGroupControl>
              </FormControl>
            </Flex>
          )}

          <Heading size="lg" mt={4}>
            Set a time
          </Heading>
          <Text>When would you like to receive the export?</Text>
          <RadioGroupControl name="exportTime.mode">
            <Flex direction="column" gap={4}>
              <Radio
                value={ExportTimeMode.Now}
                key={ExportTimeMode.Now}
                isDisabled={isEditingScheduledExport}
                onClick={() => {
                  amplitude.track("Selected time to export", {
                    export_time: ExportTimeMode.Now,
                  });
                }}
              >
                <Text>Now</Text>
                <FormHelperText>
                  Get the file as soon as the export is done
                </FormHelperText>
              </Radio>
              <Radio
                value={ExportTimeMode.Later}
                key={ExportTimeMode.Later}
                onClick={() => {
                  amplitude.track("Selected time to export", {
                    export_time: ExportTimeMode.Later,
                  });
                }}
              >
                <Text>Later</Text>
                <FormHelperText>
                  Set a time to run the export and receive it after it is done
                </FormHelperText>
              </Radio>
            </Flex>
          </RadioGroupControl>
          {props.values.exportTime.mode === ExportTimeMode.Later && (
            <Flex direction="column" ml={6} mt={4} gap={4}>
              <FormControl isInvalid={showFieldError("exportTime.presetTime")}>
                <FormLabel>When would you like to export?</FormLabel>
                <SelectControl
                  name="exportTime.presetTime"
                  selectProps={{ placeholder: "Select an option" }}
                  w="fit-content"
                >
                  {presetTimeOptions.map((presetTimeOption) => (
                    <option {...presetTimeOption} />
                  ))}
                  <option key="custom" value="custom">
                    Custom
                  </option>
                </SelectControl>
                <FormErrorMessage>
                  {props.errors.exportTime?.presetTime}
                </FormErrorMessage>
              </FormControl>
              {props.values.exportTime.presetTime === "custom" && (
                <FormControl
                  isInvalid={
                    props.values.exportTime.presetTime === "custom" &&
                    showFieldError("exportTime.customTime")
                  }
                >
                  <FormLabel>Select a date and time</FormLabel>
                  <DateTimePicker
                    timezoneName={timezoneName}
                    initialValue={props.values.exportTime.customTime}
                    onChange={(date: Date | null) => {
                      props.setFieldValue(
                        "exportTime.customTime",
                        date
                          ? fromZonedTime(date, timezoneName).toISOString()
                          : undefined,
                        true
                      );
                      props.setFieldTouched(
                        "exportTime.customTime",
                        true,
                        false
                      );
                    }}
                  />
                  <FormErrorMessage>
                    {props.errors.exportTime?.customTime}
                  </FormErrorMessage>
                </FormControl>
              )}
              <Text fontSize="sm" color="gray.600">
                Time is in {timezoneName} timezone and indicates when the export
                starts. Delivery time may vary depending on processing duration.{" "}
                <br />
                If you chose to export all responses, the number of exported
                rows may differ from your initial selection as data changes over
                time.
              </Text>
              <FormikSwitchInput<boolean>
                label="Repeats"
                name={"exportTime.repeat.enabled"}
                checked={[true, ""]}
                unchecked={[false, ""]}
                formLabelProps={{
                  flexDirection: "row-reverse",
                  fontSize: "md",
                  mb: 0,
                }}
                onChange={(event) => {
                  amplitude.track("Toggled repeat export", {
                    repeat_export: event.target.checked
                      ? "is_active"
                      : "is_inactive",
                  });
                }}
              />
              {props.values.exportTime.repeat?.enabled && (
                <RadioGroupControl
                  name="exportTime.repeat.mode"
                  isInvalid={showFieldError("exportTime.repeat.mode")}
                  ml={12}
                >
                  <Flex direction="column" gap={4}>
                    {repeatModes.map((mode) => (
                      <Radio
                        value={mode}
                        key={mode}
                        onClick={() => {
                          amplitude.track("Selected repeat cadence", {
                            repeat_cadence: mode,
                          });
                        }}
                      >
                        <Text>{REPEAT_MODE_OPTIONS[mode].label}</Text>
                        <FormHelperText>
                          {REPEAT_MODE_OPTIONS[mode].formatFormDescription(
                            getSelectedTimeInTimezone(
                              props.values,
                              timezoneName
                            ),
                            timezoneName,
                            props.values.exportTime.adjustForDST
                          )}
                        </FormHelperText>
                      </Radio>
                    ))}
                  </Flex>
                </RadioGroupControl>
              )}
              {props.values.exportTime.repeat?.enabled && (
                <Flex direction="column" gap={0}>
                  <FormikSwitchInput<boolean>
                    label="Adjust for daylight saving time"
                    name="exportTime.adjustForDST"
                    formLabelProps={{
                      flexDirection: "row-reverse",
                      fontSize: "md",
                    }}
                    checked={[true, ""]}
                  />
                  <Text ml={12} fontSize="sm" color="gray.600">
                    When enabled, the export is scheduled in your organization’s
                    timezone and adjusts for DST.
                    <br />
                    When disabled, it is scheduled at a fixed offset from UTC
                    and does not adjust for DST.
                  </Text>
                </Flex>
              )}
            </Flex>
          )}
        </>
      )}

      <Spacer minH={8} />

      <AdminFormButtons>
        <Button
          variant="outline"
          colorScheme="gray"
          onClick={() => {
            amplitude.track("Clicked back");
            props.setCurrentPage(ExportFormPage.SelectColumns);
          }}
        >
          Back
        </Button>
        <Spacer />
        <Tooltip
          label={disabledFromNoChanges ? "No changes made to save" : null}
          isDisabled={!disabledFromNoChanges}
          hasArrow
        >
          <Button
            variant="solid"
            type="submit"
            isLoading={props.isSubmitting}
            isDisabled={submitDisabled}
            onClick={() => {
              amplitude.track(
                isEditingScheduledExport
                  ? "Confirmed update scheduled export"
                  : "Confirmed custom export"
              );
            }}
          >
            {isEditingScheduledExport ? "Update" : "Export"}
          </Button>
        </Tooltip>
      </AdminFormButtons>
    </Flex>
  );
};

const GoogleDriveFolderIdInput = () => (
  <InputControlWithParsing
    flex={1}
    name="googleDrive.config.folderId"
    label="Folder URL or ID"
    inputProps={{
      placeholder: "E.g.: zRySfKjHXxnzc6yyocihlzQzhJe6qfs6Um7V or full URL",
    }}
    parseInput={extractFolderIdFromUrl}
  />
);

const GoogleSheetsSpreadsheetIdInput = () => (
  <InputControlWithParsing
    flex={1}
    name="googleDrive.config.spreadsheetId"
    label="Spreadsheet URL or ID"
    inputProps={{
      placeholder:
        "E.g.: 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upmt or full URL",
    }}
    parseInput={extractSpreadsheetIdFromUrl}
  />
);

const GoogleSheetsSheetNameInput = () => (
  <Flex flex={1} direction="column" alignItems="flex-start">
    <InputControl
      name="googleDrive.config.sheetName"
      label="Sheet name (optional)"
      inputProps={{ placeholder: "Sheet1" }}
    />
    <FormHelperText>
      If the sheet does not exist, it will be created
    </FormHelperText>
  </Flex>
);
